import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { MsalService } from '@azure/msal-angular';
import { signOutRedirectUri } from '@k12/app-services';
import { environment } from '../../environments/environment';

interface SystemUser extends Record<string, unknown> {
  name?: string;
  email?: string;
  password?: string;
  adminApiUrl?: string;
  accountId?: string;
  applicantId?: string;
  senderId?: string;
  programPartnerId?: string;
}

interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string[];
  borderColor: string[];
  borderWidth: number;
}

interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

interface ChartScale {
  beginAtZero?: boolean;
  ticks: { color: string };
  grid: { color: string; drawBorder: boolean };
}

interface ChartOptions {
  plugins: { legend: { labels: { color: string } } };
  scales: { y: ChartScale; x: ChartScale };
}

const DEV_PASSWORD = 'Password12!';
const DEV_APPLICANT_ID = 'ebe9b75e-6b02-4d89-835d-11e42c3a1111';
const DEV_SENDER_ID = 'f456b942-4377-40be-9fbc-bfef9e306a42';

@Injectable({ providedIn: 'root' })
export class SystemService {
  reloadHeader = new EventEmitter();
  private readonly environmentService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);
  private readonly msalService = inject(MsalService);

  user: SystemUser;
  user_dev: SystemUser = {
    name: 'dev',
    email: 'test@test.com',
    password: DEV_PASSWORD,
    adminApiUrl: 'https://development-api-enrollment.azure-api.net',
    accountId: 'xj7yhns98z1',
    applicantId: DEV_APPLICANT_ID,
    senderId: DEV_SENDER_ID
  };

  user_test: SystemUser = {
    name: 'testing',
    email: 'test@test.com',
    password: DEV_PASSWORD,
    adminApiUrl: 'https://testing-api-enrollment.azure-api.net',
    accountId: 'mytoken',
    applicantId: DEV_APPLICANT_ID,
    senderId: DEV_SENDER_ID
  };

  user_local: SystemUser = {
    name: 'local',
    email: 'test@local.com',
    password: DEV_PASSWORD,
    adminApiUrl: this.environmentService.adminApiUrl,
    accountId: 'mytoken',
    applicantId: DEV_APPLICANT_ID,
    senderId: DEV_SENDER_ID
  };

  headers = new HttpHeaders({
    'Ocp-Apim-Subscription-Key': 'edf6d0cd728844e79d9a8dab9e75e7a6',
    'Content-Type': 'application/json'
  });

  demoData: Record<string, string>[] = [
    {
      status: 'Needs Attention',
      name: 'Update Credentials',
      desc: "1 employee credentials couldn't be validated",
      date: '11/03/2024'
    },
    { status: 'Updated', name: 'Update NPI#', desc: 'Update NPI#', date: '11/03/2024' }
  ];

  basicData: ChartData;
  basicOptions: ChartOptions;
  systemData: SystemUser | undefined;

  constructor() {
    this.user = this.getProfile();
    this.basicData = {
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      datasets: [
        {
          label: 'Sales',
          data: [540, 325, 702, 620],
          backgroundColor: [
            'rgba(255, 159, 64, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(153, 102, 255, 0.2)'
          ],
          borderColor: ['rgb(255, 159, 64)', 'rgb(75, 192, 192)', 'rgb(54, 162, 235)', 'rgb(153, 102, 255)'],
          borderWidth: 1
        }
      ]
    };

    this.basicOptions = {
      plugins: {
        legend: {
          labels: {
            color: '#000'
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: '#ddd'
          },
          grid: {
            color: '#bdbdbd',
            drawBorder: false
          }
        },
        x: {
          ticks: {
            color: '#ddd'
          },
          grid: {
            color: '#bdbdbd',
            drawBorder: false
          }
        }
      }
    };
  }

  getDashboard(): Observable<Record<string, unknown>> {
    return this.http.get(`${this.user.adminApiUrl}/partners/accounts/dashboard`) as Observable<Record<string, unknown>>;
  }

  isAuthenticated(): Observable<boolean> {
    const token = localStorage.getItem('token');
    return of(!!token);
  }

  login(payload: SystemUser): void {
    localStorage.setItem('token', JSON.stringify(payload));
    this.user = payload;
    this.systemData = payload;
  }

  logout(): void {
    this.clearRosterAgreementStorage();
    this.clearUserGroupsCache();

    this.msalService.logoutRedirect({
      account: this.msalService.instance.getActiveAccount(),
      postLogoutRedirectUri: signOutRedirectUri(environment),
      authority: environment.msalAuthority
    });
  }

  private clearRosterAgreementStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('roster_agreement_accepted')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  private clearUserGroupsCache(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('userGroups:')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  getProfile(): SystemUser {
    return JSON.parse(localStorage.getItem('token') || '{}') as SystemUser;
  }

  loadConfig(config: string): void {
    switch (config) {
      case 'dev':
        this.user = this.user_dev;
        break;
      case 'test':
        this.user = this.user_test;
        break;
      case 'local':
        this.user = this.user_local;
        break;
    }
  }

  getUserProfile(): Observable<Record<string, unknown>> {
    return this.http.get(
      `${this.user.adminApiUrl}/personas/${this.user.applicantId}/GetProfile`
    ) as Observable<Record<string, unknown>>;
  }

  saveUserProfile(payload: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.http.put(
      `${this.user.adminApiUrl}/personas/SaveApplicantProfile`,
      payload
    ) as Observable<Record<string, unknown>>;
  }
}
